import { useRef } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import { getMessageWithPlaceholders } from 'shared-locales';
import api from 'api';
import { actions } from 'store';
import { generateRandomKey } from 'lib/helpers';
import {
  Button,
  ContentLabel,
  ContentValue,
  CopyButton,
  Link,
  ModalButtonWithForm
} from 'components/common';
import { Box } from 'components/common/box';
import {
  Checkbox,
  DisplayField,
  Domains,
  FormGroup,
  RadioGroup,
  Select,
  Switch
} from 'components/common/form';

import type { FormApi } from 'informed';

const ShareSignUpLink = () => {
  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { course },
    serverConfig: {
      adminUrl,
      accountEnableUsernameAccounts,
      courseEnableSignUpLink
    },
    theme
  } = useReduxState();

  const formApi = useRef<FormApi>(undefined);

  const generateHash = () => {
    return generateRandomKey(12, 'abcdefghijklmnopqrstuvwxyz0123456789');
  };

  const handleGenerateNewLink = () => {
    const newHash = generateHash();
    formApi.current.setValue('signUpLinkHash', newHash);
  };

  const handleSubmit = async (values: typeof initialValues) => {
    const data = {
      signUpLinkAllowAccountCreation: values.signUpLinkAllowAccountCreation,
      signUpLinkAllowAccountCreationType: values.signUpLinkAllowAccountCreation
        ? values.signUpLinkAllowAccountCreationType
        : 'any',
      signUpLinkEnabled: values.signUpLinkEnabled,
      signUpLinkHash: values.signUpLinkHash,
      signUpLinkRestrictDomains: values.signUpLinkRestrictDomainsActive
        ? values.signUpLinkRestrictDomains
        : []
    };

    await api.put(`/admin/courses/${course.id}`, data);
    const courses = await api.get(`/admin/courses?id=${course.id}`);

    dispatch(actions.updatePageState({ course: courses.data.courses[0] }));
    dispatch(
      actions.addNotice({
        text: messages.settingsSavedSuccessfully,
        type: 'success'
      })
    );
    return true;
  };

  const initialValues = {
    signUpLinkAllowAccountCreation: course.signUpLinkAllowAccountCreation,
    signUpLinkAllowAccountCreationType:
      course.signUpLinkAllowAccountCreationType,
    signUpLinkEnabled: course.signUpLinkEnabled,
    signUpLinkHash: course.signUpLinkHash || generateHash(),
    signUpLinkRestrictDomains: course.signUpLinkRestrictDomains,
    signUpLinkRestrictDomainsActive: Boolean(
      course.signUpLinkRestrictDomains?.length
    )
  };

  if (!course || !courseEnableSignUpLink) return null;

  return (
    <div className="sign-up-link">
      <Box
        title={messages.shareSignUpLink}
        titleTooltip={messages.shareSignUpLinkDescription}
      >
        <ContentLabel label={messages.commonStatus} verticalLabelSpacing={5}>
          <ContentValue
            icon={course.signUpLinkEnabled ? 'check-circle' : undefined}
            iconStyle={{ fontSize: '1.4rem' }}
            style={{
              fontSize: '1.5rem',
              fontWeight: course.signUpLinkEnabled ? 600 : 400,
              color: course.signUpLinkEnabled
                ? theme.successStandaloneColor
                : 'inherit'
            }}
          >
            {course.signUpLinkEnabled
              ? messages.commonEnabled
              : messages.commonDisabled}
          </ContentValue>
        </ContentLabel>

        {course.signUpLinkEnabled ? (
          <>
            <ContentLabel
              label={messages.shareSignUpLink}
              verticalLabelSpacing={5}
            >
              <div className="link">
                <Link
                  external={true}
                  href={`${adminUrl}/join/courses/${course.slug}/${course.signUpLinkHash}`}
                  openInNewTab={true}
                  openInNewTabIcon={false}
                  text={`${adminUrl.replace(/https?:\/\//, '')}/join/courses/${
                    course.slug
                  }/${course.signUpLinkHash}`}
                />
              </div>

              <CopyButton
                category="light"
                text={messages.commonCopyLink}
                size="small"
                style={{ marginTop: 10 }}
                successText={messages.commonCopied}
                value={`${adminUrl.replace(/https?:\/\//, '')}/join/courses/${
                  course.slug
                }/${course.signUpLinkHash}`}
              />
            </ContentLabel>

            <ContentLabel
              label={messages.commonSettings}
              verticalLabelSpacing={5}
            >
              <ContentValue
                icon="check-circle"
                iconStyle={{
                  color: theme.textColorLight,
                  fontSize: '1.2rem'
                }}
                style={{
                  color: theme.textColorLight,
                  fontSize: '1.4rem',
                  fontWeight: 600
                }}
              >
                {course.signUpLinkAllowAccountCreation
                  ? messages.shareSignUpLinkForAnyone
                  : messages.shareSignUpLinkForExisting}
              </ContentValue>

              {course.signUpLinkRestrictDomains?.length ? (
                <ContentValue
                  icon="check-circle"
                  iconStyle={{
                    color: theme.textColorLight,
                    fontSize: '1.2rem'
                  }}
                  style={{
                    color: theme.textColorLight,
                    fontSize: '1.4rem',
                    fontWeight: 600,
                    marginTop: 5
                  }}
                >
                  {getMessageWithPlaceholders({
                    message: messages.shareSignUpLinkRestrictedToDomains,
                    placeholders: {
                      numberOfDomains: course.signUpLinkRestrictDomains.length
                    }
                  })}
                </ContentValue>
              ) : null}
            </ContentLabel>
          </>
        ) : null}

        <div className="footer">
          <ModalButtonWithForm
            buttonCategory="default"
            buttonText={
              course.signUpLinkEnabled
                ? messages.commonSettings
                : messages.commonActivate
            }
            initialValues={initialValues}
            modalActionCategory="success"
            modalActionText={messages.commonSaveChanges}
            modalTitle={messages.shareSignUpLink}
            modalWidth="medium"
            onSubmit={handleSubmit}
            pristineIsValid={false}
            setFormApi={(form: FormApi) => (formApi.current = form)}
          >
            {(formState: { values: typeof initialValues }) => (
              <>
                <FormGroup
                  label={messages.shareSignUpLink}
                  labelHelpText={messages.shareSignUpLinkDescription}
                >
                  <Switch
                    field="signUpLinkEnabled"
                    id="sign-up-link-enabled"
                    label={messages.shareSignUpLinkEnable}
                    style={{ marginTop: 5 }}
                  />
                </FormGroup>

                <FormGroup
                  label={messages.commonLink}
                  style={{ marginTop: 25 }}
                >
                  <Link
                    external={true}
                    href={`${adminUrl}/join/courses/${course.slug}/${formState.values.signUpLinkHash}`}
                    openInNewTab={true}
                    openInNewTabIcon={false}
                    text={`${adminUrl.replace(
                      /https?:\/\//,
                      ''
                    )}/join/courses/${course.slug}/${
                      formState.values.signUpLinkHash
                    }`}
                  />

                  <DisplayField field="signUpLinkHash" />

                  <div style={{ marginTop: 10 }}>
                    <Button
                      category="light"
                      icon="sync-alt"
                      onClick={handleGenerateNewLink}
                      text={messages.shareGenerateNewLink}
                      size="small"
                    />
                  </div>
                </FormGroup>

                <FormGroup
                  label={messages.shareSignUpLinkFor}
                  style={{ marginTop: 25 }}
                >
                  <RadioGroup
                    field="signUpLinkAllowAccountCreation"
                    options={[
                      {
                        disabled: !formState.values.signUpLinkEnabled,
                        id: `sign-up-link-allow-account-creation-no`,
                        label: messages.shareSignUpLinkForExisting,
                        value: false
                      },
                      {
                        disabled: !formState.values.signUpLinkEnabled,
                        id: `sign-up-link-allow-account-creation-yes`,
                        label: messages.shareSignUpLinkForAnyone,
                        value: true
                      }
                    ]}
                  />
                </FormGroup>

                {accountEnableUsernameAccounts &&
                formState.values.signUpLinkAllowAccountCreation ? (
                  <FormGroup label={messages.createAccountRestriction}>
                    <Select
                      disabled={!formState.values.signUpLinkEnabled}
                      field="signUpLinkAllowAccountCreationType"
                      id="sign-up-link-allow-account-creation-type"
                      options={[
                        {
                          label: messages.createAccountRestrictionNone,
                          value: 'any'
                        },
                        {
                          label: messages.createAccountRestrictionEmail,
                          value: 'email'
                        },
                        {
                          label: messages.createAccountRestrictionUsername,
                          value: 'username'
                        }
                      ]}
                      style={{ width: 240 }}
                    />
                  </FormGroup>
                ) : null}

                <FormGroup
                  label={messages.commonRestrictions}
                  style={{ marginTop: 25 }}
                >
                  <Checkbox
                    disabled={!formState.values.signUpLinkEnabled}
                    field="signUpLinkRestrictDomainsActive"
                    id="sign-up-link-restrict-domains-active"
                    label={messages.shareSignUpLinkRestrictDomains}
                  />
                  <Domains
                    disabled={
                      !formState.values.signUpLinkEnabled ||
                      !formState.values?.signUpLinkRestrictDomainsActive
                    }
                    field="signUpLinkRestrictDomains"
                    id="sign-up-link-restrict-domains"
                    style={{ marginTop: 10 }}
                  />
                </FormGroup>
              </>
            )}
          </ModalButtonWithForm>
        </div>
      </Box>

      <style jsx>{`
        .active {
          margin-top: 20px;
        }
        .link {
          font-size: 1.6rem;
          white-space: pre-wrap;
          word-wrap: break-word;
        }
        .footer {
          display: flex;
          justify-content: flex-start;
          margin-top: 30px;
        }
      `}</style>
    </div>
  );
};

export default ShareSignUpLink;
